// packages/web-ui/src/AppShell/ContextPanel-modes/DiffMode.tsx
//
// Diff destination context-panel body.
//
// Renders the changeset list (active + history) in the side panel so the
// user can pick a changeset to inspect. Earlier iterations were a pure
// empty-state stub which left freshly-proposed changesets invisible — see
// e2e-test-report-2026-05-17.md action item #1. This implementation:
//   - calls `loadChangesetList()` on mount;
//   - subscribes to the diff slice list + status;
//   - refreshes the list whenever a `kb:changesetProposed` SSE event arrives
//     (signalled by the changeset slice's `changesetsById` count changing).

import { useEffect, useRef, type JSX } from 'react';
import { ModeChrome } from './ModeChrome.js';
import { useContextPanelStatus } from './useContextPanelStatus.js';
import { useRootStore } from '../../state/store.js';
import { navigateTo } from '../../router/hash-router.js';
import {
  selectActiveChangesets,
  selectHistoryChangesets,
} from '../../state/diff.js';
import type { AvailableUndoChangeset, UndoInFlight } from '../../state/changeset.js';

export function DiffMode(): JSX.Element {
  const status = useContextPanelStatus('diff');
  const list = useRootStore((s) => s.changesetSummariesList);
  const loadChangesetList = useRootStore((s) => s.loadChangesetList);

  // The changeset slice's count is the canonical signal that an SSE
  // `kb:changesetProposed` arrived (the dispatcher records the new
  // changeset under `changesetsById`). When it changes, refresh the
  // summary list so the side panel updates.
  const changesetCount = useRootStore((s) => Object.keys(s.changesetsById).length);

  // Undo ring buffer — drives the per-row Revert affordance. A history
  // row is revertable only when its changesetId is in this list. The
  // length is also the trigger for an extra list-refresh after an undo
  // SSE event: a successful revert drops the entry from the ring AND
  // mutates the changeset's per-entity statuses on disk (applied →
  // pending), so the cached `changesetSummariesList` row goes stale and
  // would still read "applied" until the next manual fetch. Watching
  // the ring-buffer length closes that gap with one extra GET.
  const availableUndoChangesets = useRootStore(
    (s) => s.availableUndoChangesets as AvailableUndoChangeset[],
  );
  const undoRingLength = availableUndoChangesets.length;
  const undoInFlightById = useRootStore(
    (s) => s.undoInFlightById as Record<string, UndoInFlight>,
  );
  const dispatchUndoLastChangeset = useRootStore((s) => s.dispatchUndoLastChangeset);
  const undoableIds = new Set(availableUndoChangesets.map((e) => e.changesetId));

  // Mount-time fetch (idempotent — the slice guards against concurrent loads).
  useEffect(() => {
    void loadChangesetList();
  }, [loadChangesetList]);

  // Debounce list refresh when changesets arrive via SSE OR when the
  // undo ring buffer shrinks (kb:changesetUndone landed). Without the
  // undo-trigger the history row would keep showing the stale "applied"
  // counts after a revert because the SPA never re-fetches.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (refreshTimer.current !== null) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      void loadChangesetList();
    }, 500);
    return () => {
      if (refreshTimer.current !== null) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [changesetCount, undoRingLength, loadChangesetList]);

  const active = selectActiveChangesets({ changesetSummariesList: list, changesetSummariesLoading: false, changesetSummariesError: null });
  const history = selectHistoryChangesets({ changesetSummariesList: list, changesetSummariesLoading: false, changesetSummariesError: null });

  return (
    <ModeChrome
      modeKey="diff"
      testId="diff-mode-root"
      title="Diff"
      emptyStateText="No active changeset. Ask the agent to propose changes."
      errorText="Could not load changesets."
      status={status}
    >
      <div data-testid="diff-mode-list" className="flex flex-1 flex-col gap-3 overflow-auto p-2">
        {active.length > 0 && (
          <section data-testid="diff-mode-active-section">
            <h3 className="text-taupe-700 mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide">
              Active
            </h3>
            <ul className="space-y-1">
              {active.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    data-testid={`diff-list-active-${c.id}`}
                    onClick={() => navigateTo({ kind: 'diff', changesetId: c.id })}
                    className="hover:bg-cream-100 focus-visible:ring-clay-400 w-full rounded px-2 py-1.5 text-left text-[12px] focus:outline-none focus-visible:ring-2"
                  >
                    <div className="font-medium text-stone-800">{c.intent || c.id}</div>
                    <div className="text-taupe-600 text-[10.5px]">
                      {c.totalEntities} entit{c.totalEntities === 1 ? 'y' : 'ies'} · {c.pendingCount} pending · {c.acceptedCount} accepted
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {history.length > 0 && (
          <section data-testid="diff-mode-history-section">
            <h3 className="text-taupe-700 mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide">
              History
            </h3>
            <ul className="space-y-1">
              {history.slice(0, 30).map((c) => {
                const revertable = undoableIds.has(c.id) && c.appliedCount > 0;
                const inFlight = undoInFlightById[c.id];
                const reverting =
                  inFlight !== undefined && inFlight.errorMessage === undefined;
                return (
                  <li key={c.id}>
                    <div className="hover:bg-cream-100 focus-within:bg-cream-100 flex items-stretch gap-1 rounded">
                      <button
                        type="button"
                        data-testid={`diff-list-history-${c.id}`}
                        onClick={() => navigateTo({ kind: 'diff', changesetId: c.id })}
                        className="focus-visible:ring-clay-400 min-w-0 flex-1 rounded px-2 py-1.5 text-left text-[12px] focus:outline-none focus-visible:ring-2"
                      >
                        <div className="truncate text-stone-700">{c.intent || c.id}</div>
                        <div className="text-taupe-600 text-[10.5px]">
                          {c.aggregateStatus} · {c.totalEntities} entit
                          {c.totalEntities === 1 ? 'y' : 'ies'}
                          {c.appliedCount > 0 ? ` · ${c.appliedCount} applied` : ''}
                        </div>
                      </button>
                      {revertable && (
                        <button
                          type="button"
                          data-testid={`diff-list-history-revert-${c.id}`}
                          aria-label={`Revert changeset ${c.id}`}
                          disabled={reverting}
                          onClick={() => {
                            void dispatchUndoLastChangeset(c.id);
                          }}
                          title={
                            reverting
                              ? 'Revert in progress…'
                              : 'Revert the .sysml writes from this changeset.'
                          }
                          className="text-audi-red hover:bg-accent-red-bg shrink-0 self-center rounded border border-transparent px-2 py-0.5 text-[10.5px] uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {reverting ? 'Reverting…' : 'Revert'}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </ModeChrome>
  );
}
